import { useField } from 'formik';
import React from 'react';
import Text from '@service-victoria/component-library/components/Text';
import Input from '@service-victoria/component-library/components/Input';
import Checkbox from '@service-victoria/component-library/components/Checkbox';
import { permitService } from 'services';
import * as Interfaces from 'interfaces';
import { Address } from 'Components/Address';
import { countryCodes } from 'interfaces/common';
import * as Styled from '../../../styled';
import { useAppContext } from 'context/app';
import { Props as AutoCompleteProps } from '@service-victoria/component-library/components/AutoComplete';
import {
  isGreenZoneResident,
  isOrangeZoneResident,
  isRedZoneResident
} from 'steps/Assess/travel-reasons';
import type { DeepPartial } from 'types/globals';

type LeavingFromAddressProps = {
  setDeltaValues: (values: DeepPartial<Pick<Interfaces.Common.FormValues, 'assess'>>) => void;
  isInternationalArrivalPermit: boolean;
};

export const LeavingFromAddress: React.FC<LeavingFromAddressProps> = ({
  setDeltaValues,
  isInternationalArrivalPermit
}) => {
  const [{ value: reasonCode }] = useField<string>('assess.reasons');
  const [, , { setValue: setFromAddressZone }] = useField<string>('assess.Depart.fromAddressZone');
  const [{ value: fromQuarantine }] = useField<string>('assess.Depart.quarantine.fromQuarantine');
  const [{ value: fromAddressCountry }] = useField<string>('assess.Depart.fromAddressCountry');
  const {
    currentState: { transactionID }
  } = useAppContext();

  const checkLGAZone = async (lga: string) => {
    const service = new permitService();

    try {
      const lgaZoneDetails = await service.lookupLGAZone(lga, transactionID);
      setFromAddressZone(lgaZoneDetails.zone);
    } catch (error) {
      console.error(error);
    }
  };

  const onSelection = async (
    address: Interfaces.Common.AddressWithLga,
    selectedValue: AutoCompleteProps.OptionType
  ) => {
    setDeltaValues({
      assess: {
        Depart: {
          fromAddress: {
            fullDetails: {
              id: address.id,
              suburb: address.suburbOrPlaceOrLocality || address.stateOrTerritory,
              country: address.country,
              lga: address.lga || address.regionalCouncilCode,
              state: address.stateOrTerritory,
              postCode: address.postCode,
              fullAddress: address.addressLine1
            },
            summaryData: selectedValue
          },
          fromAddressZone: ''
        }
      }
    });

    if (address.lga || address.regionalCouncilCode) {
      await checkLGAZone((address.lga || address.regionalCouncilCode) as string);
    }
  };
  return (
    <>
      <Styled.Spacer24 />
      <Text.Heading.H3 fontWeight='bold'>I&apos;m leaving from</Text.Heading.H3>
      <Styled.Spacer24 />
      <Address
        fieldName='assess.Depart.fromAddress'
        label='Address'
        onSelection={onSelection}
        country={fromAddressCountry as countryCodes}
        addressType={isInternationalArrivalPermit ? 'overseas' : 'local'}
      />
      {(isGreenZoneResident(reasonCode) ||
        isOrangeZoneResident(reasonCode) ||
        isRedZoneResident(reasonCode)) && (
        <>
          <Styled.Spacer24 />
          <Checkbox
            label='I am leaving hotel quarantine'
            name='assess.Depart.quarantine.fromQuarantine'
            data-testid='HotelQuarantine-CheckBox'
            errorLabelProps={{
              'data-testid': 'HotelQuarantine-CheckBox'
            }}
          />
        </>
      )}
      {fromQuarantine && (
        <>
          <Styled.Spacer8 />
          <Input
            label='Quarantine hotel name'
            name='assess.Depart.quarantine.hotel'
            data-testid='HotelName-Input'
            errorLabelProps={{
              'data-testid': 'HotelName-InputError'
            }}
          />
          <Styled.Spacer16 />
        </>
      )}
    </>
  );
};

export default LeavingFromAddress;
